import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { GanttChartSquare, Diamond } from 'lucide-react';
import { fetchTimeline } from '../lib/api';
import { AREE } from '../constants/areas';
import PageSkeleton from './nav/PageSkeleton';
import Breadcrumb from './nav/Breadcrumb';
import FilterBar from './nav/FilterBar';
import { useUrlFilters } from '../lib/useUrlFilters';
import Tooltip from './nav/Tooltip';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const ZOOM_OPTIONS = ['anno', 'Q1', 'Q2', 'Q3', 'Q4'];

function ymd(s) { return s ? new Date(s + 'T00:00:00Z') : null; }

function pctFromDate(iso, windowStart, windowEnd) {
  const t = ymd(iso)?.getTime();
  const a = ymd(windowStart).getTime();
  const b = ymd(windowEnd).getTime();
  if (t == null) return null;
  if (t < a) return null;       // fuori finestra a sinistra
  if (t > b) return null;       // fuori finestra a destra
  return ((t - a) / (b - a)) * 100;
}

function statoColor(stato) {
  if (stato === 'raggiunto') return 'var(--qp-success)';
  if (stato === 'a_rischio') return 'var(--qp-risk)';
  if (stato === 'on_track') return 'var(--qp-ink)';
  return 'var(--qp-text-mute)';
}

function fmt(v, u) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const s = Number.isInteger(n) ? n.toString() : n.toFixed(2).replace(/\.?0+$/, '');
  return u ? `${s} ${u}` : s;
}

function confidenceBucket(c) {
  if (c == null) return null;
  const n = Number(c);
  return n >= 0.7 ? 'alta' : n >= 0.4 ? 'media' : 'bassa';
}

export default function PaginaTimeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useUrlFilters();
  const [search, setSearch] = useSearchParams();
  const zoom = ZOOM_OPTIONS.includes(search.get('zoom')) ? search.get('zoom') : 'anno';
  const setZoom = (z) => {
    setSearch((prev) => {
      const sp = new URLSearchParams(prev);
      if (z === 'anno') sp.delete('zoom');
      else sp.set('zoom', z);
      return sp;
    }, { replace: true });
  };
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchTimeline(currentYear)
      .then((d) => { if (alive) { setData(d); setError(null); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [currentYear]);

  // Filtri applicati: area, stato (per KR), confidence (skipped per ora — niente confidence in /timeline)
  const filtered = useMemo(() => {
    if (!data) return null;
    const obs = (data.obiettivi || []).map((ob) => {
      const krs = (ob.krs || []).filter((kr) => {
        if (filters.area.length > 0) {
          const a = (kr.area || ob.area || '').toLowerCase();
          if (!filters.area.includes(a)) return false;
        }
        if (filters.stato.length > 0) {
          const isNumerico = kr.tipo === 'numerico';
          const isRaggiunto = isNumerico
            ? (kr.target_committed != null && kr.valore_attuale != null && Number(kr.valore_attuale) >= Number(kr.target_committed))
            : kr.stato === 'raggiunto';
          // Per timeline classifichiamo a-rischio se progress_pct < 50 a metà anno
          const todayPct = pctFromDate(data.today, data.year_start, data.year_end);
          const progress = kr.progress_pct;
          const isARischio = !isRaggiunto && progress != null && todayPct != null && progress + 10 < todayPct;
          const isOnTrack = !isRaggiunto && !isARischio;
          const match = filters.stato.some((s) => (
            (s === 'raggiunto' && isRaggiunto) ||
            (s === 'a_rischio' && isARischio) ||
            (s === 'on_track' && isOnTrack)
          ));
          if (!match) return false;
        }
        return true;
      });
      return { ...ob, krs };
    }).filter((ob) => ob.krs.length > 0);
    return { ...data, obiettivi: obs };
  }, [data, filters]);

  // Finestra visibile in base allo zoom (anno o Q1..Q4) — DEVE stare prima dei
  // conditional returns altrimenti viola le rules-of-hooks.
  const { windowStart, windowEnd, visibleMonths } = useMemo(() => {
    const y = currentYear;
    if (zoom === 'anno') {
      return { windowStart: `${y}-01-01`, windowEnd: `${y}-12-31`, visibleMonths: MONTHS.map((m, i) => ({ m, idx: i + 1 })) };
    }
    const q = parseInt(zoom.slice(1), 10);
    const startMonth = (q - 1) * 3 + 1;
    const endMonth = q * 3;
    const endDay = endMonth === 12 ? 31 : new Date(Date.UTC(y, endMonth, 0)).getUTCDate();
    return {
      windowStart: `${y}-${String(startMonth).padStart(2, '0')}-01`,
      windowEnd: `${y}-${String(endMonth).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`,
      visibleMonths: MONTHS.slice(startMonth - 1, endMonth).map((m, i) => ({ m, idx: startMonth + i })),
    };
  }, [zoom, currentYear]);

  if (loading && !data) {
    return <PageSkeleton variant="list" sections={3} rowsPerSection={5} testid="timeline-loading" />;
  }
  if (error && !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h2 className="font-display text-2xl tracking-tight mb-2">Errore timeline</h2>
          <p className="font-body text-sm text-soft mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="font-mono text-xs uppercase tracking-[0.2em] border border-ink px-5 py-2 hover:bg-ink hover:text-canvas transition-colors"
            data-testid="timeline-retry"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  // Finestra visibile in base allo zoom (anno o Q1..Q4)
  const todayPct = pctFromDate(filtered.today, windowStart, windowEnd);
  const totObs = (filtered.obiettivi || []).length;
  const totKrs = (filtered.obiettivi || []).reduce((acc, o) => acc + (o.krs?.length || 0), 0);
  const totalAll = (data?.obiettivi || []).reduce((acc, o) => acc + (o.krs?.length || 0), 0);
  const filtersActive = filters.area.length + filters.stato.length + filters.confidence.length > 0;

  return (
    <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10" data-testid="pagina-timeline">
      <Breadcrumb items={[{ label: 'Timeline' }]} />
      <header className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <GanttChartSquare size={20} strokeWidth={1.5} className="text-ink" />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute">Anno {currentYear}</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Timeline scadenze</h1>
        <p className="mt-3 font-body text-base text-soft max-w-2xl">
          Tutti gli obiettivi, KR e tappe del {currentYear} disposti sull&apos;asse temporale.
          Diamante <Diamond size={11} className="inline -mt-0.5" strokeWidth={1.8} /> = milestone, barra colorata = avanzamento.
        </p>
        <div className="mt-4 font-mono text-xs text-soft">
          {filtersActive
            ? <>{totKrs} di {totalAll} key result · {totObs} obiettivi</>
            : <>{totKrs} key result · {totObs} obiettivi</>}
        </div>
      </header>

      <FilterBar filters={filters} onChange={setFilters} hide={['confidence']} />

      {/* Zoom segmented control */}
      <div className="flex items-center gap-2 mb-2" data-testid="timeline-zoom">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mute mr-1">Zoom</span>
        {ZOOM_OPTIONS.map((z) => {
          const active = zoom === z;
          return (
            <button
              key={z}
              onClick={() => setZoom(z)}
              className={
                'font-mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border transition-colors ' +
                (active
                  ? 'border-ink bg-ink text-canvas'
                  : 'border-line text-soft hover:border-ink hover:text-ink')
              }
              data-testid={`timeline-zoom-${z}`}
              aria-pressed={active}
            >
              {z === 'anno' ? 'Anno' : z}
            </button>
          );
        })}
      </div>

      {/* Asse mesi */}
      <div className="mt-6 sticky top-0 z-20 bg-canvas">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-1 md:gap-4">
          <div className="hidden md:block font-mono text-[10px] uppercase tracking-[0.18em] text-mute py-2">Obiettivo · KR</div>
          <div
            className="relative h-8 border-b border-line grid"
            style={{ gridTemplateColumns: `repeat(${visibleMonths.length}, minmax(0, 1fr))` }}
          >
            {visibleMonths.map(({ m, idx }, i) => (
              <div
                key={idx}
                className={'font-mono text-[10px] uppercase tracking-[0.18em] text-soft flex items-center justify-center border-l ' + (i === 0 ? 'border-transparent' : 'border-linesoft')}
                data-testid={`timeline-month-${idx}`}
              >
                {m}
              </div>
            ))}
            {/* Linea OGGI */}
            {todayPct != null && (
              <div
                className="absolute top-0 bottom-0 border-l-2 border-risk/70"
                style={{ left: `${todayPct}%` }}
                data-testid="timeline-today-line"
                title={`Oggi · ${filtered.today}`}
              />
            )}
          </div>
        </div>
      </div>

      {/* Righe */}
      <div className="mt-2 space-y-1" data-testid="timeline-rows">
        {(filtered.obiettivi || []).length === 0 && (
          <div className="border border-dashed border-line p-10 text-center font-body text-sm text-mute" data-testid="timeline-empty">
            {filtersActive ? 'Nessun KR soddisfa i filtri attivi.' : 'Nessun KR con scadenza definita per il ' + currentYear}
          </div>
        )}
        {(filtered.obiettivi || []).map((ob) => {
          const meta = AREE[(ob.area || '').toLowerCase()] || { color: 'var(--qp-text-soft)', label: '—' };
          return (
            <div key={ob.id} className="mt-6" data-testid={`timeline-ob-${ob.id}`}>
              {/* Obiettivo header */}
              <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-1 md:gap-4 mb-1">
                <Link
                  to={`/obiettivo/${ob.id}`}
                  className="flex items-center gap-2 group"
                  data-testid={`timeline-ob-link-${ob.id}`}
                >
                  <span
                    className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border shrink-0"
                    style={{ borderColor: meta.color, color: meta.color }}
                  >
                    {meta.label}
                  </span>
                  <span className="font-display text-sm tracking-tight truncate group-hover:underline">{ob.titolo}</span>
                </Link>
                <div className="h-2 border-b border-linesoft" />
              </div>

              {/* KR rows */}
              {ob.krs.map((kr) => {
                const startPct = (kr.tipo === 'numerico')
                  ? 0
                  : pctFromDate(windowStart, windowStart, windowEnd) ?? 0;
                const endPct = pctFromDate(kr.scadenza, windowStart, windowEnd);
                const isRaggiunto = kr.tipo === 'numerico'
                  ? (kr.target_committed != null && kr.valore_attuale != null && Number(kr.valore_attuale) >= Number(kr.target_committed))
                  : kr.stato === 'raggiunto';
                const isNumerico = kr.tipo === 'numerico';
                const progress = kr.progress_pct;
                const isARischio = !isRaggiunto && progress != null && todayPct != null && progress + 10 < todayPct;
                const barColor = isRaggiunto ? 'var(--qp-success)' : isARischio ? 'var(--qp-risk)' : meta.color;
                // Scadenza fuori finestra: rendiamo la barra fino a 100% (clamp)
                const scadenzaInWindow = endPct != null;
                const barEnd = scadenzaInWindow ? endPct : 100;

                return (
                  <div
                    key={kr.id}
                    className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-1 md:gap-4 md:items-center py-2 hover:bg-linesoft transition-colors group"
                    data-testid={`timeline-kr-${kr.id}`}
                  >
                    {/* Titolo + tipo */}
                    <Link
                      to={`/obiettivo/${ob.id}`}
                      className="flex items-center gap-2 min-w-0 pl-2"
                      title={kr.titolo}
                    >
                      <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-mute border border-line px-1 py-0.5 shrink-0">
                        {kr.tipo}
                      </span>
                      <span
                        className="font-body text-sm truncate group-hover:text-ink"
                        style={{ textDecoration: isRaggiunto ? 'line-through' : 'none', opacity: isRaggiunto ? 0.55 : 1 }}
                      >
                        {kr.titolo}
                      </span>
                    </Link>

                    {/* Traccia gantt */}
                    <div className="relative h-7 bg-canvas border border-linesoft">
                      {/* Griglia mese */}
                      <div
                        className="absolute inset-0 grid pointer-events-none"
                        style={{ gridTemplateColumns: `repeat(${visibleMonths.length}, minmax(0, 1fr))` }}
                      >
                        {visibleMonths.map(({ idx }, i) => (
                          <div key={idx} className={i === 0 ? '' : 'border-l border-linesoft'} />
                        ))}
                      </div>
                      {/* Today line */}
                      {todayPct != null && (
                        <div className="absolute top-0 bottom-0 border-l-2 border-risk/40 pointer-events-none" style={{ left: `${todayPct}%` }} />
                      )}
                      {/* Barra avanzamento */}
                      {isNumerico && (
                        <div
                          className="absolute top-1.5 bottom-1.5 left-0 opacity-30"
                          style={{ width: `${barEnd}%`, backgroundColor: barColor }}
                          title={`Scadenza ${kr.scadenza}`}
                        />
                      )}
                      {isNumerico && progress != null && (
                        <div
                          className="absolute top-1.5 bottom-1.5 left-0"
                          style={{ width: `${Math.min(progress, todayPct || 100, barEnd)}%`, backgroundColor: barColor }}
                          title={`Avanzamento ${Math.round(progress)}%`}
                        />
                      )}
                      {!isNumerico && (
                        <div
                          className="absolute top-1.5 bottom-1.5"
                          style={{ left: `${startPct}%`, width: `${Math.max(2, barEnd - startPct)}%`, backgroundColor: barColor, opacity: isRaggiunto ? 0.4 : 0.18 }}
                        />
                      )}
                      {/* Milestones */}
                      {(kr.milestones || []).map((ms, idx) => {
                        const left = pctFromDate(ms.scadenza_inferred, windowStart, windowEnd);
                        if (left == null) return null;
                        const color = ms.raggiunto ? 'var(--qp-success)' : statoColor(ms.on_track);
                        const lbl = [
                          `${ms.etichetta}`,
                          ms.suggerito != null ? `Suggerito: ${fmt(ms.suggerito, kr.unita)}` : null,
                          ms.reale != null ? `Reale: ${fmt(ms.reale, kr.unita)}` : null,
                          ms.raggiunto ? '✓ raggiunto' : `stato: ${ms.on_track}`,
                        ].filter(Boolean).join('\n');
                        return (
                          <Tooltip key={ms.id || `${kr.id}-${ms.etichetta}-${idx}`} label={lbl} side="top" wide maxWidth={220} delay={200}>
                            <span
                              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 inline-block w-2.5 h-2.5 rotate-45 border border-canvas shadow"
                              style={{ left: `${left}%`, backgroundColor: color }}
                              data-testid={`timeline-milestone-${kr.id}-${idx}`}
                            />
                          </Tooltip>
                        );
                      })}
                      {/* Scadenza KR (diamante grande) */}
                      {scadenzaInWindow && (
                        <Tooltip
                          label={`Scadenza ${kr.scadenza}\n${isNumerico ? `Target: ${fmt(kr.target_committed, kr.unita)} · attuale: ${fmt(kr.valore_attuale, kr.unita)}` : (isRaggiunto ? '✓ raggiunto' : 'in corso')}`}
                          side="top"
                          wide
                          maxWidth={260}
                          delay={200}
                        >
                          <span
                            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 inline-block"
                            style={{ left: `${endPct}%` }}
                            data-testid={`timeline-scadenza-${kr.id}`}
                          >
                            <Diamond size={14} strokeWidth={2} className="text-ink" fill={isRaggiunto ? 'var(--qp-success)' : barColor} />
                          </span>
                        </Tooltip>
                      )}
                      {/* Etichetta % */}
                      {isNumerico && progress != null && (
                        <span className="absolute right-1 top-1/2 -translate-y-1/2 font-mono text-[9px] tabular-nums text-mute bg-canvas/70 px-1">
                          {Math.round(progress)}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Legenda */}
      <div className="mt-10 pt-6 border-t border-line flex flex-wrap items-center gap-6 font-mono text-[10px] uppercase tracking-[0.15em] text-soft" data-testid="timeline-legend">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rotate-45 bg-ink" /> Milestone
        </span>
        <span className="flex items-center gap-1.5">
          <Diamond size={11} strokeWidth={2} className="text-ink" fill="currentColor" /> Scadenza KR
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-2 bg-ok" /> Raggiunto
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-2 bg-risk" /> A rischio
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-px h-3 bg-risk/100/70 mr-1" /> Oggi
        </span>
      </div>
    </div>
  );
}
